import { useCallback, useRef, useState, useEffect } from 'react';

import type { FindChangedEvent } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';

/**
 * Custom hook to manage search feature for AgGrid
 * 
 * @returns Object containing all state and functions needed for search
 */
export const useGridSearch = (options?: { autoShowOnCtrlF?: boolean }) => {
  const { autoShowOnCtrlF = true } = options || {};
  const gridRef = useRef<AgGridReact>(null);
  const [findSearchValue, setFindSearchValue] = useState<string>();
  const [activeMatchNum, setActiveMatchNum] = useState<string>();
  const [isSearchVisible, setIsSearchVisible] = useState<boolean>(false);

  const onFindChanged = useCallback((event: FindChangedEvent) => {
    const { activeMatch, totalMatches, findSearchValue: eventFindSearchValue } = event;
    setActiveMatchNum(
      eventFindSearchValue?.length
        ? `${activeMatch?.numOverall ?? 0}/${totalMatches}`
        : "",
    );
  }, []);

  // Navigation functions
  const next = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.findNext();
    }
  }, []);

  const previous = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.findPrevious();
    }
  }, []);

  const goToFind = useCallback((num: number) => {
    if (Number.isNaN(num) || num < 0 || !gridRef.current?.api) {
      return;
    }
    gridRef.current.api.findGoTo(num);
  }, []);

  // Clear search function
  const clearSearch = useCallback(() => {
    setFindSearchValue('');
    setActiveMatchNum('');
  }, []);

  // Toggle search visibility
  const toggleSearch = useCallback(() => {
    setIsSearchVisible(prev => !prev);
  }, []);

  // Show search
  const showSearch = useCallback(() => {
    setIsSearchVisible(true);
  }, []);

  // Hide search
  const hideSearch = useCallback(() => {
    setIsSearchVisible(false);
  }, []);

  // Handle Ctrl+F keyboard shortcut
  useEffect(() => {
    if (!autoShowOnCtrlF) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault();
        showSearch();
        // Focus on input after showing
        setTimeout(() => {
          const searchInput = document.querySelector('input[placeholder*="検索"]') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
          }
        }, 100);
      }
      
      // ESC to hide search
      if (event.key === 'Escape') {
        hideSearch();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [autoShowOnCtrlF, showSearch, hideSearch]);

  return {
    // Grid reference
    gridRef,
    
    // Search state
    findSearchValue,
    setFindSearchValue,
    activeMatchNum,
    isSearchVisible,
    
    // Event handlers
    onFindChanged,
    
    // Navigation functions
    next,
    previous,
    goToFind,
    clearSearch,
    
    // Visibility control functions
    toggleSearch,
    showSearch,
    hideSearch,
    
    // Props for AgGridReact
    gridSearchProps: {
      findSearchValue,
      onFindChanged,
    },
    
    // Props for SearchBox component
    searchBoxProps: {
      gridRef,
      findSearchValue,
      setFindSearchValue,
      activeMatchNum,
      onFindChanged,
      isVisible: isSearchVisible,
      onToggle: toggleSearch,
      onShow: showSearch,
      onHide: hideSearch,
    },
  };
};
